import React from 'react';
import { SelectedMetric, MetricStats } from '../types';

interface StatsPanelProps {
  selectedMetrics: SelectedMetric[];
  stats: Record<string, MetricStats>;
  visibleRange?: { from: Date | null; to: Date | null };
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    background: 'rgba(33, 40, 44, 0.8)',
    borderRadius: '8px',
    border: '1px solid rgba(108, 117, 125, 0.3)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid rgba(108, 117, 125, 0.2)',
    background: 'rgba(33, 40, 44, 0.5)',
    cursor: 'pointer',
  },
  title: {
    color: '#c5c5c5',
    fontSize: '0.8rem',
    fontWeight: 500,
    margin: 0,
  },
  content: {
    padding: '0.75rem',
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.75rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.4rem 0.5rem',
    borderBottom: '1px solid rgba(108, 117, 125, 0.2)',
    color: '#8b949e',
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
  },
  thRight: {
    textAlign: 'right' as const,
    padding: '0.4rem 0.5rem',
    borderBottom: '1px solid rgba(108, 117, 125, 0.2)',
    color: '#8b949e',
    fontWeight: 500,
    whiteSpace: 'nowrap' as const,
  },
  td: {
    padding: '0.4rem 0.5rem',
    borderBottom: '1px solid rgba(108, 117, 125, 0.1)',
    color: '#c5c5c5',
  },
  tdRight: {
    padding: '0.4rem 0.5rem',
    borderBottom: '1px solid rgba(108, 117, 125, 0.1)',
    color: '#c5c5c5',
    textAlign: 'right' as const,
    fontFamily: 'monospace',
  },
  metricName: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  colorIndicator: {
    width: '8px',
    height: '8px',
    borderRadius: '2px',
    flexShrink: 0,
  },
  anomaly: {
    color: '#ff6b6b',
    fontSize: '0.65rem',
    marginLeft: '0.25rem',
  },
  noData: {
    color: '#6c757d',
    padding: '1rem',
    textAlign: 'center' as const,
    fontSize: '0.8rem',
  },
};

export function StatsPanel({
  selectedMetrics,
  stats,
  visibleRange,
  collapsed = false,
  onToggleCollapse,
}: StatsPanelProps) {
  const visibleMetrics = selectedMetrics.filter(m => m.visible);

  const formatValue = (value: number): string => {
    if (Math.abs(value) >= 10000) {
      return value.toExponential(2);
    }
    if (Math.abs(value) >= 1000) {
      return value.toFixed(1);
    }
    if (Math.abs(value) >= 100) {
      return value.toFixed(2);
    }
    if (Math.abs(value) >= 1) {
      return value.toFixed(3);
    }
    return value.toFixed(4);
  };

  const isAnomaly = (key: string, currentStats: MetricStats): boolean => {
    // Flag if current avg is > 3 std devs from historical
    const threshold = currentStats.avg + 3 * currentStats.stdDev;
    return currentStats.max > threshold;
  };

  if (collapsed) {
    return (
      <div style={styles.container}>
        <div style={styles.header} onClick={onToggleCollapse}>
          <h4 style={styles.title}>Statistics</h4>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8b949e" strokeWidth="2">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header} onClick={onToggleCollapse}>
        <h4 style={styles.title}>
          Statistics
          {visibleRange?.from && visibleRange?.to && (
            <span style={{ fontWeight: 400, color: '#6c757d', marginLeft: '0.5rem' }}>
              (visible range)
            </span>
          )}
        </h4>
        {onToggleCollapse && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8b949e" strokeWidth="2">
            <path d="M19 9l-7 7-7-7" />
          </svg>
        )}
      </div>

      {/* Stats table */}
      <div style={styles.content}>
        {visibleMetrics.length === 0 ? (
          <div style={styles.noData}>Select metrics to see statistics</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Metric</th>
                <th style={styles.thRight}>Min</th>
                <th style={styles.thRight}>Max</th>
                <th style={styles.thRight}>Avg</th>
                <th style={styles.thRight}>Std Dev</th>
                <th style={styles.thRight}>Count</th>
              </tr>
            </thead>
            <tbody>
              {visibleMetrics.map(metric => {
                const metricStats = stats[metric.key];
                if (!metricStats) {
                  return (
                    <tr key={metric.key}>
                      <td style={styles.td}>
                        <div style={styles.metricName}>
                          <div style={{ ...styles.colorIndicator, background: metric.color }} />
                          {metric.label}
                        </div>
                      </td>
                      <td style={styles.tdRight} colSpan={5}>
                        <span style={{ color: '#6c757d' }}>No data</span>
                      </td>
                    </tr>
                  );
                }

                const hasAnomaly = isAnomaly(metric.key, metricStats);

                return (
                  <tr key={metric.key}>
                    <td style={styles.td}>
                      <div style={styles.metricName}>
                        <div style={{ ...styles.colorIndicator, background: metric.color }} />
                        {metric.label}
                        {hasAnomaly && <span style={styles.anomaly}>!</span>}
                      </div>
                    </td>
                    <td style={styles.tdRight}>{formatValue(metricStats.min)}</td>
                    <td style={{
                      ...styles.tdRight,
                      color: hasAnomaly ? '#ff6b6b' : '#c5c5c5',
                    }}>
                      {formatValue(metricStats.max)}
                    </td>
                    <td style={styles.tdRight}>{formatValue(metricStats.avg)}</td>
                    <td style={styles.tdRight}>{formatValue(metricStats.stdDev)}</td>
                    <td style={styles.tdRight}>{metricStats.count.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default StatsPanel;
